import { LockKeyhole, LockKeyholeOpen, PenLine, TriangleAlert } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The lock for the levels below as a quiet toggle on the provenance line (D3, R3): one write
 * with the current value. Its name says what a click does ("Lock for levels below: <label>",
 * "Unlock: <label>"), so it carries no pressed state.
 */
export function LockToggle(props: {
  locked: boolean;
  label: string;
  /** `lock` (the backoffice) or `lockEveryTenant` (the console). */
  copy: "lock" | "lockEveryTenant";
  disabled?: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const Icon = props.locked ? LockKeyhole : LockKeyholeOpen;
  const words = `settingsControls.${props.copy}` as const;
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      aria-label={
        props.locked
          ? t(`${words}.unlockName`, { label: props.label })
          : t(`${words}.lockName`, { label: props.label })
      }
      disabled={props.disabled}
      className="-my-1 h-6 text-fg-secondary"
      onClick={props.onToggle}
    >
      <Icon aria-hidden="true" strokeWidth={1.75} />
      {props.locked ? t(`${words}.unlock`) : t(`${words}.lock`)}
    </Button>
  );
}

/** A quiet action on the provenance line (reset to inherited). */
export function RowAction(props: {
  children: ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      aria-label={props.label}
      disabled={props.disabled}
      className="-my-1 h-6 text-fg-secondary"
      // Keeps focus in a typed number: its blur would commit the draft the reset replaces.
      onMouseDown={(e) => e.preventDefault()}
      onClick={props.onClick}
    >
      {props.children}
    </Button>
  );
}

/**
 * Asks before a loosening is saved (D5, R6): a warning strip under the row naming the effect,
 * with the confirm and keep actions. Focus moves to its first button when it opens; the row's
 * control is described by it meanwhile.
 */
export function LoosenConfirm(props: {
  id: string;
  label: string;
  sentence: string;
  confirm: string;
  keep: string;
  onConfirm: () => void;
  onKeep: () => void;
}) {
  const { t } = useTranslation();
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    first.current?.focus();
  }, []);
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of two actions, not a fieldset.
    <div
      id={props.id}
      role="group"
      aria-label={t("settingsControls.confirm.group", { label: props.label })}
      className="flex flex-col gap-2.5 rounded-lg border border-warning-border/60 bg-warning-bg px-3 py-2.5 @xl:flex-row @xl:items-center @xl:justify-between"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          props.onKeep();
        }
      }}
    >
      <p className="flex items-start gap-2 text-sm text-warning-fg">
        <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
        <span>{props.sentence}</span>
      </p>
      <span className="flex shrink-0 flex-wrap gap-2">
        <Button ref={first} type="button" size="sm" onClick={props.onConfirm}>
          {props.confirm}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={props.onKeep}>
          {props.keep}
        </Button>
      </span>
    </div>
  );
}

/** The named button that opens an explicit editor ("Edit levels: Classification levels"). */
export function EditorButton(props: {
  action: string;
  label: string;
  disabled?: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      size="xs"
      variant="outline"
      aria-label={t("settingsControls.editorName", { action: props.action, label: props.label })}
      disabled={props.disabled}
      onClick={props.onOpen}
    >
      <PenLine aria-hidden="true" strokeWidth={1.75} />
      {props.action}
    </Button>
  );
}

/**
 * An open explicit editor under its row (lists and structured values): the editor, then what
 * the surface adds (a review, a refusal), then Save and Cancel unless the surface words its own.
 */
export function EditorDisclosure(props: {
  labelledBy: string;
  children: ReactNode;
  /** Under the editor, above the actions. */
  notices?: ReactNode;
  /** The surface's own actions, in place of Save and Cancel. */
  actions?: ReactNode;
  /** No actions at all, while a strip in `notices` asks and acts in their place. */
  actionsHidden?: boolean;
  saveDisabled?: boolean;
  saving?: boolean;
  className?: string;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <form
      className={cn("flex flex-col gap-3", props.className)}
      aria-labelledby={props.labelledBy}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        props.onSubmit();
      }}
    >
      {props.children}
      {props.notices}
      {props.actionsHidden
        ? null
        : (props.actions ?? (
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" size="sm" disabled={props.saveDisabled || props.saving}>
                {props.saving ? t("settings.saving") : t("settings.save")}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={props.onCancel}>
                {t("settings.cancel")}
              </Button>
            </div>
          ))}
    </form>
  );
}
